const slugify = (value) => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

const pluralize = (count, singular, plural = `${singular}s`) => (
  count === 1 ? singular : plural
);

const groupTripValues = (trips, field, eyebrow) => {
  const grouped = new Map();

  trips.forEach((trip) => {
    const pluralField = field === 'country' ? 'countries' : `${field}s`;
    const values = trip[pluralField] || [trip[field]];

    values.forEach((value) => {
      const existing = grouped.get(value) || [];
      existing.push(trip);
      grouped.set(value, existing);
    });
  });

  return [...grouped.entries()].map(([value, relatedTrips]) => ({
    id: `${slugify(eyebrow)}-${slugify(value)}`,
    title: value,
    eyebrow,
    description: `${relatedTrips.length} ${pluralize(relatedTrips.length, 'Reckon trip')} connected to this ${eyebrow.toLowerCase()}.`,
    details: relatedTrips.length === 1
      ? relatedTrips.map((trip) => `${trip.tripName} · ${trip.date}`)
      : [],
    tripSlugs: relatedTrips.map((trip) => trip.slug),
    sourceRoutes: relatedTrips
      .filter((trip) => trip.sourceRoute)
      .map((trip) => ({
        label: relatedTrips.length > 1 ? `${trip.tripName} · ${trip.date}` : trip.tripName,
        route: trip.sourceRoute,
      })),
  }));
};

const tripToStatItem = (trip) => ({
  id: trip.slug,
  title: trip.tripName,
  eyebrow: trip.tripType,
  description: `${trip.destination}, ${trip.country}`,
  details: [
    trip.date,
    `${trip.teamDinners.length} ${pluralize(trip.teamDinners.length, 'team dinner')}`,
    `${trip.badges.length} ${pluralize(trip.badges.length, 'merit badge')}`,
  ],
  tripSlug: trip.slug,
  sourceRoutes: trip.sourceRoute
    ? [{ label: trip.tripName, route: trip.sourceRoute }]
    : [],
});

const buildPassportStatCollections = (trips, additionalTeamDinners = []) => {
  const adventures = trips.map(tripToStatItem);
  const countries = groupTripValues(trips, 'country', 'Country');
  const destinations = groupTripValues(trips, 'destination', 'Destination');
  const badges = trips.flatMap((trip) => trip.badges.map((badge) => {
    const badgeRecord = typeof badge === 'string' ? { name: badge } : badge;
    return {
      ...badgeRecord,
      id: badgeRecord.id || `${trip.slug}-${slugify(badgeRecord.name)}`,
      name: badgeRecord.name,
      title: badgeRecord.name,
      eyebrow: 'Merit Badge',
      description: badgeRecord.description || trip.tripName,
      details: [trip.date, `${trip.destination}, ${trip.country}`],
      tripName: trip.tripName,
      tripSlug: trip.slug,
      date: trip.date,
      sourceRoutes: trip.sourceRoute
        ? [{ label: trip.tripName, route: trip.sourceRoute }]
        : [],
    };
  }));
  const tripTeamDinners = trips.flatMap((trip) => trip.teamDinners.map((dinner) => ({
    ...dinner,
    title: dinner.name,
    eyebrow: 'Team Dinner',
    description: dinner.description || trip.tripName,
    details: [trip.date, `${trip.destination}, ${trip.country}`],
    tripName: trip.tripName,
    tripSlug: trip.slug,
    date: trip.date,
    sourceRoutes: trip.sourceRoute
      ? [{ label: trip.tripName, route: trip.sourceRoute }]
      : [],
  })));
  const teamDinners = [
    ...tripTeamDinners,
    ...additionalTeamDinners.map((dinner) => ({
      ...dinner,
      title: dinner.name,
      eyebrow: dinner.eyebrow || 'Team Dinner',
      description: dinner.description || dinner.eventName,
      details: dinner.details || [dinner.date, dinner.location].filter(Boolean),
      sourceRoutes: dinner.sourceRoute
        ? [{ label: dinner.eventName, route: dinner.sourceRoute }]
        : [],
    })),
  ];
  const conferences = trips
    .filter((trip) => trip.tripType === 'Conference')
    .map(tripToStatItem);
  const retreats = trips
    .filter((trip) => trip.tripType === 'Corporate Retreat')
    .map(tripToStatItem);

  return {
    countries: {
      key: 'countries',
      label: 'Countries Visited',
      description: 'Unique countries represented across this crew member’s Reckon trips.',
      icon: 'mdi-earth',
      count: countries.length,
      items: countries,
    },
    destinations: {
      key: 'destinations',
      label: 'Destinations Explored',
      description: 'Unique cities, regions, islands, and countries used as trip destinations.',
      icon: 'mdi-map-marker-radius-outline',
      count: destinations.length,
      items: destinations,
    },
    adventures: {
      key: 'adventures',
      label: 'Reckon Adventures',
      description: 'Every Reckon trip attended by this crew member.',
      icon: 'mdi-airplane',
      count: adventures.length,
      items: adventures,
    },
    badges: {
      key: 'badges',
      label: 'Merit Badges',
      description: 'Every merit badge earned across this crew member’s trips.',
      icon: 'mdi-seal-variant',
      count: badges.length,
      items: badges,
    },
    'team-dinners': {
      key: 'team-dinners',
      label: 'Team Dinners',
      description: 'The individual dinner records currently contributing to the passport total.',
      icon: 'mdi-silverware-fork-knife',
      count: teamDinners.length,
      items: teamDinners,
    },
    conferences: {
      key: 'conferences',
      label: 'Conferences',
      description: 'Trips categorized as conferences.',
      icon: 'mdi-microphone-variant',
      count: conferences.length,
      items: conferences,
    },
    retreats: {
      key: 'retreats',
      label: 'Retreats',
      description: 'Trips categorized as corporate retreats.',
      icon: 'mdi-compass-outline',
      count: retreats.length,
      items: retreats,
    },
  };
};

module.exports = {
  buildPassportStatCollections,
};
